import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import MuiAlert from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAllCommunities } from "../api/comunity/GetAll_comunity";
import { createCommunity } from "../api/comunity/Create_comunity";
import { updateCommunity } from "../api/comunity/Update_comminucation";
import { deleteCommunity } from "../api/comunity/Delete_comunity";

function Community() {
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem("userdata")) || {};
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const defaultRole = userData.level === 1 ? "admin" : "user";
  const [role] = useState(defaultRole);
  const [data, setData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const confirmDelete = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null && deleteIndex !== undefined) {
      handleDelete(deleteIndex);
    } else {
      showSnackbar("ไม่สามารถลบได้: ไม่พบข้อมูล", "error");
    }
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await getAllCommunities();
      if (res.status === 200) {
        setData(res.data);
      } else {
        showSnackbar("โหลดข้อมูลไม่สำเร็จ", "error");
      }
    };
    fetchData();
  }, []);

  const handleChange = (index, key, value) => {
    const updated = [...data];
    updated[index][key] = value;
    setData(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const appCommId = `CM${timestamp}`;
    const newRow = {
      appCommId,
      commName: "",
      province: "",
      amphur: "",
      tambon: "",
      postCode: "",
      totalMembers: 0,
      noOfRais: 0,
      noOfTrees: 0,
      forecastYieldKg: 0,
    };
    setData((prev) => [...prev, newRow]);
  };

  const handleDelete = async (index) => {
    try {
      const appCommId = data[index].appCommId;
      const id = data[index].id;
      if (id) {
        const res = await deleteCommunity(appCommId);
        if (res.status !== 200) {
          showSnackbar("ลบไม่สำเร็จ", "error");
          return;
        }
      }
      setData((prev) => prev.filter((_, i) => i !== index));
      showSnackbar("ลบข้อมูลเรียบร้อย", "success");
    } catch (err) {
      showSnackbar("เกิดข้อผิดพลาดขณะลบ", "error");
    }
  };
  const handleSave = async () => {
    try {
      const existingIds = new Set(
        (await getAllCommunities()).data.map((d) => d.appCommId)
      );

      for (const item of data) {
        if (existingIds.has(item.appCommId)) {
          await updateCommunity(item);
        } else {
          await createCommunity(item);
        }
      }

      showSnackbar("บันทึกข้อมูลสำเร็จ", "success");

      const refreshed = await getAllCommunities();
      if (refreshed.status === 200) {
        setData(refreshed.data);
      }
    } catch {
      showSnackbar("เกิดข้อผิดพลาดขณะบันทึก", "error");
    }
  };

  const renderTextField = (
    label,
    value,
    onChange,
    readOnly = false,
    type = "text"
  ) => (
    <TextField
      label={label}
      fullWidth
      type={type}
      size="small"
      margin="dense"
      InputProps={{ disableUnderline: true, readOnly }}
      sx={{
        input: { color: "#000", WebkitTextFillColor: "#000" },
        label: { color: "#000" },
      }}
      value={value}
      onChange={onChange}
    />
  );

  return (
    <Box p={2}>
      <Typography variant="h6" mb={2}>
        ข้อมูลกลุ่มวิสาหกิจชุมชน
      </Typography>

      {isMobile ? (
        data.map((row, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{row.commName || "ยังไม่มีชื่อกลุ่ม"}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderTextField(
                "รหัส",
                row.appCommId,
                (e) => handleChange(index, "appCommId", e.target.value),
                true
              )}
              {renderTextField("ชื่อวิสาหกิจ", row.commName, (e) =>
                handleChange(index, "commName", e.target.value)
              )}
              {renderTextField("จังหวัด", row.province, (e) =>
                handleChange(index, "province", e.target.value)
              )}
              {renderTextField("อำเภอ", row.amphur, (e) =>
                handleChange(index, "amphur", e.target.value)
              )}
              {renderTextField("ตำบล", row.tambon, (e) =>
                handleChange(index, "tambon", e.target.value)
              )}
              {renderTextField("รหัสไปรษณีย์", row.postCode, (e) =>
                handleChange(index, "postCode", e.target.value)
              )}
              {renderTextField(
                "สมาชิก",
                row.totalMembers,
                (e) => handleChange(index, "totalMembers", e.target.value),
                false,
                "number"
              )}
              {renderTextField(
                "จำนวนไร่",
                row.noOfRais,
                (e) => handleChange(index, "noOfRais", e.target.value),
                false,
                "number"
              )}
              {renderTextField(
                "จำนวนต้นไม้",
                row.noOfTrees,
                (e) => handleChange(index, "noOfTrees", e.target.value),
                false,
                "number"
              )}
              {renderTextField(
                "ผลผลิตที่คาดการณ์ (กก.)",
                row.forecastYieldKg,
                (e) => handleChange(index, "forecastYieldKg", e.target.value),
                false,
                "number"
              )}
              <Button
                variant="outlined"
                color="error"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => confirmDelete(index)}
              >
                ลบรายการนี้
              </Button>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>ชื่อ</TableCell>
                <TableCell>จังหวัด</TableCell>
                <TableCell>อำเภอ</TableCell>
                <TableCell>ตำบล</TableCell>
                <TableCell>ไปรษณีย์</TableCell>
                <TableCell>สมาชิก</TableCell>
                <TableCell>ไร่</TableCell>
                <TableCell>ต้นไม้</TableCell>
                <TableCell>ผลผลิต(กก.)</TableCell>
                {role === "admin" && <TableCell>ลบ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  {[
                    "commName",
                    "province",
                    "amphur",
                    "tambon",
                    "postCode",
                    "totalMembers",
                    "noOfRais",
                    "noOfTrees",
                    "forecastYieldKg",
                  ].map((key) => (
                    <TableCell key={key}>
                      <TextField
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          readOnly: role !== "admin",
                          sx: {
                            minWidth: "130px",
                            "& .MuiInputBase-input.Mui-disabled": {
                              color: "#000",
                              WebkitTextFillColor: "#000",
                            },
                          },
                        }}
                        size="small"
                        value={row[key]}
                        type={typeof row[key] === "number" ? "number" : "text"}
                        onChange={(e) =>
                          handleChange(index, key, e.target.value)
                        }
                        disabled={role !== "admin" || key === "appCommId"}
                      />
                    </TableCell>
                  ))}
                  {role === "admin" && (
                    <TableCell align="center">
                      <IconButton
                        onClick={() => confirmDelete(index)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {role === "admin" && (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            เพิ่ม
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            บันทึก
          </Button>
        </Box>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default Community;
